import React from "react"
import PropTypes from "prop-types"

// Material UI
import { makeStyles } from "@material-ui/core/styles"
import { Box, Container, Grid, Typography } from "@material-ui/core"

// Icons
import LinkedInIcon from "@material-ui/icons/LinkedIn"
import TwitterIcon from "@material-ui/icons/Twitter"
import FacebookIcon from "@material-ui/icons/Facebook"
import YouTubeIcon from "@material-ui/icons/YouTube"
import InstagramIcon from "@material-ui/icons/Instagram"

import { getPlatformURL } from "shared/helpers"

const xPadding2 = {
  xs: 2,
  sm: 5,
  md: 10,
  xl: 20
}

const yPadding = {
  xs: 4,
  sm: 7,
  md: 9,
  xl: 15
}

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "#0F172A" // aluminium (40%)
  },
  logo: {
    width: 80
  },
  address: {
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(5)
    },
    color: "#94A3B8",
    marginTop: theme.spacing(8),

    "& p": {
      fontSize: 18,
      color: "#94A3B8"
    },

    "& .country": {
      marginBottom: theme.spacing(0.75),
      fontSize: 16,
      color: "#FFFFFF",
      letterSpacing: 0.8
    }
  },
  locationsHeading: {
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
    color: "#FFFFFF"
  },
  categoryHeading: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: 18,
    color: "#FFFFFF",
    letterSpacing: 0.9,
    textTransform: "uppercase",
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(3)
    },
    marginBottom: theme.spacing(3)
  },
  categoryContent: {
    "& a": {
      color: "#94A3B8",
      fontSize: 18,
      [theme.breakpoints.down("sm")]: {
        marginBottom: theme.spacing(1)
      },
      marginBottom: theme.spacing(1.5),

      display: "flex",
      alignItems: "center",

      "&:hover": {
        textDecoration: "underline"
      },

      "& svg": {
        fontSize: 28,
        marginRight: theme.spacing(2)
      }
    }
  }
}))

const footerData = {
  learn: [
    {
      text: "All Programmes",
      link: getPlatformURL("suraasa", "/store"),
      isExternal: false
    },
    {
      text: "PgCTL",
      link: getPlatformURL(
        "suraasa",
        "/store/qualifications/professional-graduate-certificate-in-teaching-learning"
      ),
      isExternal: false
    }
  ],
  jobs: [
    {
      text: "Explore Jobs",
      link: getPlatformURL("jobs", "/explore"),
      isExternal: false
    },
    // { text: "Partner Schools", link: getPlatformURL("jobs", "/explore") },
    {
      text: "Hire from Suraasa",
      link: getPlatformURL("suraasa", "/jobs/school"),
      isExternal: false
    }
  ],
  resources: [
    {
      text: "FAQs",
      link: getPlatformURL("suraasa", "/faqs"),
      isExternal: false
    },
    // { text: "All Resources", link: "https://www.suraasa.com/resources" },
    {
      text: "Blog",
      link: getPlatformURL("suraasa", "/blog"),
      isExternal: false
    },
    {
      text: "Masterclasses",
      link: getPlatformURL("suraasa", "/masterclasses"),
      isExternal: false
    },
    {
      text: "Newsletter",
      link: getPlatformURL("suraasa", "/newsletter"),
      isExternal: false
    },
    { text: "Podcast", link: "https://anchor.fm/suraasa", isExternal: true },
    {
      text: "All Resources",
      link: getPlatformURL("suraasa", "/resources"),
      isExternal: false
    }
  ],
  more: [
    // { text: "About Us", link: "" },
    {
      text: "In the News",
      link: getPlatformURL("suraasa", "/in-the-news"),
      isExternal: false
    },
    {
      text: "Success Stories",
      link: getPlatformURL("suraasa", "/success-stories"),
      isExternal: false
    },
    {
      text: "Careers",
      link: getPlatformURL("suraasa", "/careers"),
      isExternal: false
    }
  ],
  termsOfUse: [
    {
      text: "Terms of Use",
      link: getPlatformURL("suraasa", "/terms-of-use"),
      isExternal: false
    }
  ],
  privacyPolicy: [
    {
      text: "Privacy Policy",
      link: getPlatformURL("suraasa", "/privacy-policy"),
      isExternal: false
    }
  ],
  contactUs: [
    {
      text: "Talk to a Mentor",
      link: getPlatformURL(
        "suraasa",
        `/talk-to-a-mentor?utm_source=footer&utm_medium=footer&utm_campaign=Talk to a mentor&utm_content=&utm_term=&audience=&source_url=${
          typeof window === "undefined"
            ? ""
            : window.location.origin + window.location.pathname
        }`
      ),
      isExternal: false
    }
  ]
}

export default function Footer() {
  const classes = useStyles()

  return (
    <>
      <Box px={xPadding2} py={yPadding} className={classes.root}>
        <Container maxWidth="xl" disableGutters>
          <Grid container>
            <Grid item xs={12} md={3}>
              <a href="/">
                <img
                  className={classes.logo}
                  alt="suraasa-logo"
                  src="/store/assets/logos/mini.webp"
                />
              </a>
            </Grid>
            {/* Spacer */}
            <Grid item md={1} />

            <Grid item container xs={12} md={8}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Typography className={classes.categoryHeading}>
                  Learn
                </Typography>

                <div className={classes.categoryContent}>
                  {footerData.learn.map((x, i) => (
                    <Typography
                      component="a"
                      href={x.link}
                      variant="body1"
                      key={i}
                    >
                      {x.text}
                    </Typography>
                  ))}
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Typography className={classes.categoryHeading}>
                  Jobs
                </Typography>

                <div className={classes.categoryContent}>
                  {footerData.jobs.map((x, i) => (
                    <Typography
                      component="a"
                      href={x.link}
                      variant="body1"
                      key={i}
                    >
                      {x.text}
                    </Typography>
                  ))}
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Typography className={classes.categoryHeading}>
                  Resources
                </Typography>

                <div className={classes.categoryContent}>
                  {footerData.resources.map((x, i) => (
                    <Typography
                      component="a"
                      href={x.link}
                      variant="body1"
                      key={i}
                    >
                      {x.text}
                    </Typography>
                  ))}
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Typography className={classes.categoryHeading}>
                  More
                </Typography>

                <div className={classes.categoryContent}>
                  {footerData.more.map((x, i) => (
                    <Typography
                      component="a"
                      href={x.link}
                      variant="body1"
                      key={i}
                    >
                      {x.text}
                    </Typography>
                  ))}
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Typography className={classes.categoryHeading}>
                  connect with us
                </Typography>

                <div className={classes.categoryContent}>
                  <Typography
                    component="a"
                    href="https://suraasa.co/linkedin"
                    variant="body1"
                  >
                    <LinkedInIcon /> LinkedIn
                  </Typography>
                  <Typography
                    component="a"
                    href="https://suraasa.co/twitter"
                    variant="body1"
                  >
                    <TwitterIcon /> Twitter
                  </Typography>
                  <Typography
                    component="a"
                    href="https://suraasa.co/youtube"
                    variant="body1"
                  >
                    <YouTubeIcon /> YouTube
                  </Typography>
                  <Typography
                    component="a"
                    href="https://suraasa.co/instagram"
                    variant="body1"
                  >
                    <InstagramIcon /> Instagram
                  </Typography>
                  <Typography
                    component="a"
                    href="https://suraasa.co/facebook"
                    variant="body1"
                  >
                    <FacebookIcon /> Facebook
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        px={xPadding2}
        py={{ xs: 1, md: 2.25 }}
        style={{ background: "#0F172A", color: "#94A3B8" }}
      >
        <Container maxWidth="xl" disableGutters>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              justifyContent: "space-between"
            }}
          >
            <Typography variant="body1" align="center">
              Copyright © {new Date().getFullYear()} Les Transformations
              Learning Pvt. Ltd.
            </Typography>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8
              }}
            >
              <Typography
                component="a"
                variant="body1"
                href={footerData.termsOfUse[0].link}
              >
                {footerData.termsOfUse[0].text}
              </Typography>
              <div className={classes.dot} />
              <Typography
                component="a"
                variant="body1"
                href={footerData.privacyPolicy[0].link}
              >
                {footerData.privacyPolicy[0].text}
              </Typography>
            </div>
          </div>
        </Container>
      </Box>
    </>
  )
}

Footer.propTypes = {
  props: PropTypes.any
}
Footer.defaultProps = {}
